import { PageBodyProps } from '@pypestream/design-system';
import React, { Suspense } from 'react';
import { useParams } from 'react-router';

import { Loader } from '../components';
import {
  Actions,
  Builder,
  ChannelCreate,
  Channels,
  ChannelSettings,
  DefaultMessages,
  Files,
  Home,
  Performance,
  ProjectConfigurations,
  Versions,
  VoiceChannelSettings,
} from '../pages';
import { ProjectAreaSidenav } from '../pages/project-settings/project-area-sidenav';
import { useProStudioCtxSelector } from '../xstate/app.xstate';

const PypeDesignerPage = React.lazy(() =>
  import('../pages/pype-designer').then((m) => ({
    default: m.PypeDesignerPage,
  }))
);

export enum ProStudioPages {
  HOME = 'Home',
  CONFIGURATIONS = 'Configurations',
  VERSIONS = 'Versions',
  WORKFLOWS = 'Workflows',
  CHANNELS = 'Channels',
  ACTIONS = 'Actions',
  FILES = 'Files',
  DEFAULT_MESSAGES = 'Default Messages',
  CHANNEL_SETTINGS = 'Channel settings',
  CHANNEL_CREATION = 'Channel creation',
  VOICE_CHANNEL_CREATION = 'Voice Channel Creation',
  VOICE_CHANNEL_SETTINGS = 'Voice Channel Settings',
  PYPE_DESIGNER = 'Pype Designer',
  PERFORMANCE = 'Performance',
}

export interface StudioPageProps {
  pageBodyProps?: PageBodyProps;
  loading?: boolean;
  label?: string;
  setLoading?: (loading: boolean) => void;
}

export const useProStudioRoutes = () => {
  const {
    botId: botIdParam,
    channelId: channelIdParam,
    projectId: projectIdParam,
  } = useParams();

  const { botIdCtx, channelIdCtx, projectIdCtx } = useProStudioCtxSelector(
    ({ botId, channelId, project }) => ({
      botIdCtx: botId,
      channelIdCtx: channelId,
      projectIdCtx: project?.id,
    })
  );

  const botId = botIdParam || botIdCtx;
  const channelId = channelIdParam || channelIdCtx;
  const projectId = projectIdParam || projectIdCtx;

  const botBasePath = projectId && botId ? `/${projectId}/${botId}` : undefined;

  const commonPageBodyProps: PageBodyProps = {
    width: 'xlarge',
    background: 'none',
  };

  return {
    [ProStudioPages.HOME.toLowerCase()]: {
      path: projectId ? `/${projectId}` : '/',
      label: 'Solutions',
      schema: ':projectId',
      element: (
        <>
          <ProjectAreaSidenav />
          <Home pageBodyProps={{ background: 'none' }} />
        </>
      ),
    },
    [ProStudioPages.CONFIGURATIONS.toLowerCase()]: {
      path: `/${projectId}/configurations`,
      label: ProStudioPages.CONFIGURATIONS,
      schema: ':projectId/configurations',
      element: <ProjectConfigurations pageBodyProps={{ background: 'none' }} />,
    },
    [ProStudioPages.VERSIONS.toLowerCase()]: {
      path: botBasePath || '',
      label: ProStudioPages.VERSIONS,
      schema: ':projectId/:botId',
      element: <Versions pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.WORKFLOWS.toLowerCase()]: {
      path: botBasePath ? `${botBasePath}/workflows` : '',
      label: ProStudioPages.WORKFLOWS,
      schema: ':projectId/:botId/workflows',
      element: (
        <Builder
          pageBodyProps={{
            ...commonPageBodyProps,
            spacing: 'none',
            background: 'none',
          }}
        />
      ),
    },
    [ProStudioPages.CHANNELS.toLowerCase()]: {
      path: botBasePath ? `${botBasePath}/channels` : '',
      label: ProStudioPages.CHANNELS,
      schema: ':projectId/:botId/channels',
      element: <Channels pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.CHANNEL_SETTINGS.toLowerCase().replace(/\s+/g, '_')]: {
      path: botBasePath ? `${botBasePath}/channels/${channelId}` : '',
      label: ProStudioPages.CHANNEL_SETTINGS,
      schema: ':projectId/:botId/channels/:channelId',
      // `large` (Wrapper: a fixed 1246px, vs the 1072px default) is set on BOTH
      // channel routes, not just the designer. The Pype Next tab needs the extra
      // width — at 1072px its preview stage lands under the widget's 768px
      // breakpoint, so the floating panel renders as a full-stage takeover and
      // `shell.size` is ignored by design — and the two must match, or the shared
      // tab bar shifts horizontally as you move between tabs.
      //
      // `large` and not `xlarge`: `xlarge` is `calc(100% - 64px)`, i.e. full
      // bleed, which stretched these form columns across the whole display. 1246px
      // is enough for the stage to clear the breakpoint (see
      // `preview-stage-floor.test.ts`) and only 174px wider here.
      element: (
        <ChannelSettings
          pageBodyProps={{ background: 'none', width: 'large' }}
        />
      ),
    },
    [ProStudioPages.CHANNEL_CREATION.toLowerCase().replace(/\s+/g, '_')]: {
      path: botBasePath ? `${botBasePath}/channels/create` : '',
      label: ProStudioPages.CHANNEL_CREATION,
      schema: ':projectId/:botId/channels/create',
      element: <ChannelCreate pageBodyProps={{ background: 'none' }} />,
    },
    [ProStudioPages.ACTIONS.toLowerCase()]: {
      path: botBasePath ? `${botBasePath}/actions` : '',
      label: ProStudioPages.ACTIONS,
      schema: ':projectId/:botId/actions',
      element: <Actions pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.FILES.toLowerCase()]: {
      path: botBasePath ? `${botBasePath}/files` : '',
      label: ProStudioPages.FILES,
      schema: ':projectId/:botId/files',
      element: <Files pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.DEFAULT_MESSAGES.toLowerCase().replace(/\s+/g, '_')]: {
      path: botBasePath ? `${botBasePath}/default-messages` : '',
      label: ProStudioPages.DEFAULT_MESSAGES,
      schema: ':projectId/:botId/default-messages',
      element: <DefaultMessages pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.VOICE_CHANNEL_CREATION.toLowerCase().replace(/\s+/g, '_')]:
      {
        path: botBasePath ? `${botBasePath}/voice-channel/create` : '',
        label: ProStudioPages.VOICE_CHANNEL_CREATION,
        schema: ':projectId/:botId/voice-channel/create',
        element: (
          <VoiceChannelSettings pageBodyProps={{ background: 'none' }} />
        ),
      },
    [ProStudioPages.VOICE_CHANNEL_SETTINGS.toLowerCase().replace(/\s+/g, '_')]:
      {
        path: botBasePath ? `${botBasePath}/voice-channel/:channelId` : '',
        label: ProStudioPages.VOICE_CHANNEL_SETTINGS,
        schema: ':projectId/:botId/voice-channel/:channelId',
        element: (
          <VoiceChannelSettings pageBodyProps={{ background: 'none' }} />
        ),
      },
    [ProStudioPages.PERFORMANCE.toLowerCase()]: {
      path: '/performance',
      label: ProStudioPages.PERFORMANCE,
      schema: 'performance',
      element: <Performance pageBodyProps={commonPageBodyProps} />,
    },
    [ProStudioPages.PYPE_DESIGNER.toLowerCase().replace(/\s+/g, '_')]: {
      path: botBasePath
        ? `${botBasePath}/channels/${channelId}/pype-designer`
        : '',
      label: ProStudioPages.PYPE_DESIGNER,
      schema: ':projectId/:botId/channels/:channelId/pype-designer',
      element: (
        <Suspense fallback={<Loader />}>
          {/* Same body as CHANNEL_SETTINGS above, deliberately. This route is
              the "Pype Next Theme" tab of that page and renders the same tab
              bar; a different `width`/`spacing` put the bar in a different
              place, so it visibly jumped left and up on the way in
              (PSTUDIO-3768). Keep them in step — including `width`. */}
          <PypeDesignerPage
            pageBodyProps={{ background: 'none', width: 'large' }}
          />
        </Suspense>
      ),
    },
  };
};
